import React, { useState } from 'react';
import { Download, CheckCircle, BookOpen, Volume2, Target, TrendingUp, X } from './Icons';
import { offlineLearningData } from '../data/offlineLearningData';

const OfflineLearningPage = ({ onBack, userPreferences }) => {
  const [downloadingCourses, setDownloadingCourses] = useState(new Set());

  const { displayLanguage } = userPreferences;
  const data = offlineLearningData;

  const getText = (textObj) => {
    return textObj[displayLanguage] || textObj.en;
  };

  const getIcon = (iconName, size = 24) => {
    const icons = {
      'download': <Download size={size} className="text-white" />,
      'check-circle': <CheckCircle size={size} className="text-white" />,
      'book-open': <BookOpen size={size} className="text-white" />,
      'headphones': <Volume2 size={size} className="text-white" />,
      'target': <Target size={size} className="text-white" />,
      'trending-up': <TrendingUp size={size} className="text-white" />
    };
    return icons[iconName] || <Download size={size} className="text-white" />;
  };

  const getFeatureColor = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500'
    };
    return colors[color] || colors.blue;
  };

  const handleDownload = (courseId) => {
    setDownloadingCourses(prev => new Set(prev).add(courseId));
    // Simulate download completion after 3 seconds
    setTimeout(() => {
      setDownloadingCourses(prev => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    }, 3000);
  };

  const renderMainView = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <div className="w-6"></div>
          <h1 className="text-xl font-bold">{getText(data.overview.title)}</h1>
          <button 
            onClick={onBack}
            className="flex items-center text-white hover:text-blue-300 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Download Content Section */}
        <div className="px-4 mb-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">{getText(data.downloadContent.title)}</h3>
              <Download size={20} className="text-blue-400" />
            </div>
            <p className="text-sm text-gray-300 mb-4">{getText(data.downloadContent.subtitle)}</p>
            
            {/* Progress Bar */}
            <div className="mb-3">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-blue-300">{data.downloadContent.progress.used} 已使用</span>
                <span className="text-gray-300">{data.downloadContent.progress.downloadedItems}/{data.downloadContent.progress.totalItems}</span>
                <span className="text-green-300">剩餘 {data.downloadContent.progress.remaining}</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${data.downloadContent.progress.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Downloadable Content */}
        <div className="px-4 mb-6">
          <h3 className="text-lg font-semibold mb-4">{getText(data.downloadableContent.title)}</h3>
          <div className="space-y-3">
            {data.downloadableContent.courses.map((course) => (
              <div 
                key={course.id}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      course.status === 'downloaded' ? 'bg-green-500' : 'bg-blue-500'
                    }`}>
                      {course.status === 'downloading' ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        getIcon(course.icon, 20)
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold">{getText(course.title)}</h4>
                      <p className="text-sm text-gray-300">{course.size}</p>
                    </div>
                  </div>
                  {course.status === 'available' && (
                    <button
                      onClick={() => handleDownload(course.id)}
                      disabled={downloadingCourses.has(course.id)}
                      className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${
                        downloadingCourses.has(course.id) 
                          ? 'bg-gray-500 cursor-not-allowed' 
                          : 'bg-blue-500 hover:bg-blue-600'
                      }`}
                    >
                      {downloadingCourses.has(course.id) ? '下載中...' : '下載'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Offline Features */}
        <div className="px-4 mb-6">
          <h3 className="text-lg font-semibold mb-4">{getText(data.offlineFeatures.title)}</h3>
          <div className="grid grid-cols-2 gap-3">
            {data.offlineFeatures.features.map((feature) => (
              <div 
                key={feature.id}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center"
              >
                <div className={`w-12 h-12 ${getFeatureColor(feature.color)} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  {getIcon(feature.icon, 24)}
                </div>
                <h4 className="font-semibold text-sm mb-1">{getText(feature.title)}</h4>
                <p className="text-xs text-gray-300">{getText(feature.subtitle)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return renderMainView();
};

export default OfflineLearningPage;
